// Mock data for E2E testing
// This file provides mock API responses when running in E2E test mode

export const mockCustomers = [
  {
    id: '1',
    name: 'Acme Corporation',
    responsible_user: 'john@risika.com',
    local_id: 'DK-12345',
    created_at: '2024-01-15T10:00:00Z',
    accounts: 5,
    last_login: '2024-02-10T14:30:00Z',
    users: [],
  },
  {
    id: '2',
    name: 'Tech Innovators ApS',
    responsible_user: 'sarah@risika.com',
    local_id: 'DK-67890',
    created_at: '2024-02-01T09:00:00Z',
    accounts: 3,
    last_login: '2024-02-12T08:15:00Z',
    users: [],
  },
  {
    id: '3',
    name: 'Nordic Solutions',
    responsible_user: 'mike@risika.com',
    local_id: 'SE-11111',
    created_at: '2023-11-20T11:30:00Z',
    accounts: 8,
    last_login: '2024-02-13T16:45:00Z',
    users: [],
  },
  {
    id: '4',
    name: 'Global Trade Ltd',
    responsible_user: 'emma@risika.com',
    local_id: 'NO-22222',
    created_at: '2023-09-05T14:00:00Z',
    accounts: 2,
    last_login: '2024-02-08T12:00:00Z',
    users: [],
  },
  {
    id: '5',
    name: 'Finance Pro',
    responsible_user: 'john@risika.com',
    local_id: 'DK-33333',
    created_at: '2024-01-25T16:00:00Z',
    accounts: 6,
    last_login: '2024-02-11T10:30:00Z',
    users: [],
  },
]

export const mockGlobalZones = [
  {
    id: '1',
    name: 'Nordic Region',
    countries: ['DK', 'SE', 'NO', 'FI'],
  },
  {
    id: '2',
    name: 'DACH Region',
    countries: ['DE', 'AT', 'CH'],
  },
  {
    id: '3',
    name: 'Ireland',
    countries: ['IE'],
  },
]

export const mockGlobalDataUsage = {
  companies: [
    {
      company_id: 1,
      name: 'Acme Corporation',
      country: 'DK',
      local_id: '12345678',
      plan: 'ENTERPRISE',
      zones: [
        {
          name: 'Nordic Region',
          countries: ['dk', 'se', 'no', 'fi'],
          allocation: 500,
          usage: 320,
        },
        {
          name: 'DACH Region',
          countries: ['de', 'at', 'ch'],
          allocation: 200,
          usage: 40,
        },
      ],
      total_allocation: 700,
      total_usage: 360,
    },
    {
      company_id: 2,
      name: 'Globex ApS',
      country: 'SE',
      local_id: '87654321',
      plan: 'PRO',
      zones: [
        {
          name: 'Nordic Region',
          countries: ['dk', 'se', 'no', 'fi'],
          allocation: 100,
          usage: 98,
        },
      ],
      total_allocation: 100,
      total_usage: 98,
    },
  ],
  totals: {
    companies: 2,
    allocation: 800,
    usage: 458,
    remaining: 342,
  },
}

export const mockRisikaUsers = [
  { id: '1', email: 'john@risika.com', name: 'John Smith' },
  { id: '2', email: 'sarah@risika.com', name: 'Sarah Johnson' },
  { id: '3', email: 'mike@risika.com', name: 'Mike Anderson' },
  { id: '4', email: 'emma@risika.com', name: 'Emma Wilson' },
]

export const mockCustomerData = {
  id: '1',
  name: 'Acme Corporation',
  responsible_user: 'john@risika.com',
  local_id: 'DK-12345',
  created_at: '2024-01-15T10:00:00Z',
  accounts: 5,
  users: [
    { id: 'u1', email: 'admin@acme.com', name: 'Admin User' },
    { id: 'u2', email: 'user@acme.com', name: 'Regular User' },
  ],
}

// Check if running in E2E test mode - checks URL param and localStorage
// Escalations queue. Deliberately mixed: rows with and without a resolvable
// company name, rows raised before the escalating user was recorded (empty
// escalated_by_users), several countries including one with a long official
// name (GB), and enough rows to page.
const escalationSeed: {
  local_id: string
  country: string
  company_name: string | null
  status: 'OPEN' | 'REASON_RECORDED' | 'CLOSED'
  reason: string | null
  users: { api_user_id: number; name: string | null; email: string | null }[]
}[] = [
  {
    local_id: 'de-100200300',
    country: 'DE',
    company_name: 'Mueller Energie GmbH',
    status: 'OPEN',
    reason: null,
    users: [{ api_user_id: 815, name: 'Jane Doe', email: 'jane@acme.test' }],
  },
  {
    local_id: 'fr-552081317',
    country: 'FR',
    company_name: 'Dupont Logistique SA',
    status: 'REASON_RECORDED',
    reason: 'Registry has not published the 2025 filing yet',
    users: [{ api_user_id: 816, name: 'Bob Roe', email: 'bob@beta.test' }],
  },
  {
    local_id: 'gb-08123456',
    country: 'GB',
    company_name: 'Thames Trading Ltd',
    status: 'OPEN',
    reason: null,
    // Raised before user capture existed.
    users: [],
  },
  {
    local_id: 'ref_158273052',
    country: 'US',
    company_name: null,
    status: 'CLOSED',
    reason: 'Company dissolved; no further filings expected',
    users: [{ api_user_id: 817, name: 'Cara Poe', email: 'cara@gamma.test' }],
  },
  {
    local_id: 'de-400500600',
    country: 'DE',
    company_name: 'Berlin Bau AG',
    status: 'OPEN',
    reason: null,
    users: [{ api_user_id: 815, name: 'Jane Doe', email: 'jane@acme.test' }],
  },
]

export const mockEscalations = escalationSeed
  .flatMap((seed, seedIndex) =>
    // Three variants of each seed, so pagination has something to page.
    [0, 1, 2].map((copy) => ({
      id: `escalation-${seedIndex}-${copy}`,
      country: seed.country,
      local_id: copy === 0 ? seed.local_id : `${seed.local_id}-${copy}`,
      company_name: seed.company_name
        ? `${seed.company_name}${copy === 0 ? '' : ` ${copy + 1}`}`
        : null,
      status: seed.status,
      reason: seed.reason,
      reason_recorded_by: seed.reason ? 'cs@risika.com' : null,
      escalation_count: copy + 1,
      escalated_at: '2026-01-04T09:12:00',
      last_escalated_at: `2026-0${(seedIndex % 3) + 1}-1${copy}T11:40:00`,
      updated_at: '2026-03-01T11:40:00',
      escalated_by:
        seedIndex % 2 === 0
          ? [{ company_id: 1, name: 'Acme Corporation' }]
          : [
              { company_id: 1, name: 'Acme Corporation' },
              { company_id: 2, name: 'Tech Innovators ApS' },
            ],
      escalated_by_users: seed.users,
    })),
  )
  .sort((a, b) => b.last_escalated_at.localeCompare(a.last_escalated_at))

export const isE2ETestMode = (): boolean => {
  if (typeof window === 'undefined') return false

  // Check URL query parameter first
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('e2e') === 'true') {
    return true
  }

  // Check localStorage (for internal navigation)
  if (localStorage.getItem('E2E_TEST_MODE') === 'true') {
    return true
  }

  return false
}

// Nordic lookups usage (ENG2-2465) for E2E mock mode: customers on the 2026
// packages, pre-sorted closest to the cap first like the server.
export const mockNordicLookupsUsage = {
  companies: [
    {
      company_id: 7754,
      name: 'Over Cap A/S',
      country: 'DK',
      local_id: '29384756',
      plan: 'MANUAL_PACKAGE',
      subscription_start: '2025-10-20',
      cap: 1200,
      used: 1250,
      remaining: 0,
      percent: 104,
      unlimited: false,
      over_cap: true,
      period_start: '2025-10-20',
      period_end: '2026-10-20',
      days_left: 15,
    },
    {
      company_id: 8894,
      name: 'Norlix ApS',
      country: 'DK',
      local_id: '45475433',
      plan: 'TRIAL_V2',
      subscription_start: '2026-09-18',
      cap: 50,
      used: 45,
      remaining: 5,
      percent: 90,
      unlimited: false,
      over_cap: false,
      period_start: '2026-09-18',
      period_end: '2027-09-18',
      days_left: 348,
    },
    {
      company_id: 8868,
      name: 'Strategic Nord AB',
      country: 'SE',
      local_id: '5566778899',
      plan: 'STRATEGIC_PACKAGE',
      subscription_start: '2026-03-10',
      cap: 1200,
      used: 300,
      remaining: 900,
      percent: 25,
      unlimited: false,
      over_cap: false,
      period_start: '2026-03-10',
      period_end: '2027-03-10',
      days_left: 156,
    },
    {
      company_id: 8895,
      name: 'Unmetered Trial ApS',
      country: 'DK',
      local_id: '41234567',
      plan: 'TRIAL_V2',
      subscription_start: '2026-05-01',
      cap: 2147483647,
      used: 0,
      remaining: null,
      percent: null,
      unlimited: true,
      over_cap: false,
      period_start: '2026-05-01',
      period_end: '2027-05-01',
      days_left: 208,
    },
  ],
  totals: {
    companies: 4,
    cap: 2450,
    used: 1595,
    remaining: 905,
    unlimited: 1,
    over_cap: 1,
  },
}

export const mockNordicLookupWindows = [
  {
    country: 'DK',
    local_id: '37677892',
    first_lookup_at: '2026-09-20T10:15:00',
    last_lookup_at: '2026-09-22T08:00:00',
    lookup_count: 3,
    first_route: '/{country}/company/basics/{local_id}',
    source: 'live',
  },
  {
    country: 'DK',
    local_id: '10403782',
    first_lookup_at: '2026-09-19T09:00:00',
    last_lookup_at: '2026-09-19T09:00:00',
    lookup_count: 1,
    first_route: '/monitor/add',
    source: 'monitoring',
  },
  {
    country: 'SE',
    local_id: '5560360793',
    first_lookup_at: '2026-09-18T12:00:00',
    last_lookup_at: '2026-09-18T12:00:00',
    lookup_count: 1,
    first_route: null,
    source: 'backfill',
  },
]

// A customer's lookups from the trail (ENG2-2849) for E2E mock mode.
const lookupMetrics = (
  uniqueWith: number,
  uniqueWithout: number,
  dailyWith: number,
  dailyWithout: number,
) => ({
  unique_companies: uniqueWith + uniqueWithout,
  unique_with_financial: uniqueWith,
  unique_without_financial: uniqueWithout,
  daily_lookups: dailyWith + dailyWithout,
  daily_with_financial: dailyWith,
  daily_without_financial: dailyWithout,
})

export const mockLookupUsage = {
  company_id: 1,
  year: 2026,
  month: null,
  years: [2026],
  first_lookup_at: '2026-10-16T12:20:00+00:00',
  last_lookup_at: '2026-11-03T09:30:00+00:00',
  totals: lookupMetrics(480, 2200, 1100, 2900),
  months: [
    { month: '2026-10', ...lookupMetrics(400, 1700, 800, 2100) },
    { month: '2026-11', ...lookupMetrics(150, 700, 300, 800) },
  ],
  countries: [
    { country: 'SE', ...lookupMetrics(300, 1500, 700, 2000) },
    { country: 'DK', ...lookupMetrics(180, 700, 400, 900) },
  ],
  channels: [
    { channel: 'api', ...lookupMetrics(470, 2190, 1090, 2880) },
    { channel: 'dashboard', ...lookupMetrics(10, 15, 10, 20) },
  ],
  endpoints: [
    {
      endpoint: 'Company basics',
      counted: true,
      financial: false,
      unique_companies: 2500,
      daily_lookups: 3700,
    },
    {
      endpoint: 'Financial numbers',
      counted: true,
      financial: true,
      unique_companies: 480,
      daily_lookups: 1100,
    },
    {
      endpoint: 'Employees',
      counted: false,
      financial: false,
      unique_companies: 40,
      daily_lookups: 55,
    },
  ],
}
